import { Component, EventEmitter, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TableActionItem } from '@ui-kit/webkit/components/crud-table/crud-table.model';
import { CrudAtokuUser } from '../models/crud-atoku-user.model';
import { AtokuUser } from '../models/atoku-user.model';
import { GiveAccessDialogComponent } from './dialogs/give-access-dialog/give-access-dialog.component';
import { AtokuCard, AtokuService } from '../services/atoku.service';
import { forkJoin } from 'rxjs';
import {
    DepositDeductionDialogComponent,
    DepositDeductionDialogResult
} from './dialogs/deposit-deduction-dialog/deposit-deduction-dialog.component';
import { ConsoleContextService } from '@security/console-context.service';

type SubscriptionState = 'subscribed' | 'suspended' | 'NaN';

@Component({
    selector: 'app-atoku-users',
    templateUrl: './atoku-users.component.html',
    styleUrls: ['./atoku-users.component.scss']
})
export class AtokuUsersComponent implements OnInit {
    readonly crudUserModel = CrudAtokuUser;

    tableData: CrudAtokuUser[] = [];

    private readonly refreshUsersAction = new EventEmitter<void>();
    private readonly giveAccessAction = new EventEmitter<CrudAtokuUser>();
    private readonly manageDepositAction = new EventEmitter<CrudAtokuUser>();
    private readonly toggleSubscriptionAction = new EventEmitter<CrudAtokuUser>();
    private readonly toggleBlockedAction = new EventEmitter<CrudAtokuUser>();
    private readonly removeUserAction = new EventEmitter<CrudAtokuUser>();

    readonly additionalActions: TableActionItem[] = [
        {
            icon: 'refresh',
            label: 'Refresh list',
            actionDataSource: 'None',
            enabled: 'Always',
            includeData: false,
            eventEmitter: this.refreshUsersAction,
            keepSelectedRowOnClick: false
        },
        {
            icon: 'key',
            label: 'Give Access',
            actionDataSource: 'SelectedRow',
            enabled: 'RowSelected',
            includeData: true,
            eventEmitter: this.giveAccessAction,
            keepSelectedRowOnClick: false
        },
        {
            icon: 'account_balance_wallet',
            label: 'Deposit',
            actionDataSource: 'SelectedRow',
            enabled: 'RowSelected',
            includeData: true,
            eventEmitter: this.manageDepositAction,
            keepSelectedRowOnClick: true
        },
        {
            icon: 'sync_alt',
            label: 'Toggle Subscription',
            actionDataSource: 'SelectedRow',
            enabled: 'RowSelected',
            includeData: true,
            eventEmitter: this.toggleSubscriptionAction,
            keepSelectedRowOnClick: false
        },
        {
            icon: 'block',
            label: 'Toggle Blocked',
            actionDataSource: 'SelectedRow',
            enabled: 'RowSelected',
            includeData: true,
            eventEmitter: this.toggleBlockedAction,
            keepSelectedRowOnClick: false
        },
        {
            icon: 'delete',
            label: 'Remove',
            actionDataSource: 'SelectedRow',
            enabled: 'RowSelected',
            includeData: true,
            eventEmitter: this.removeUserAction,
            keepSelectedRowOnClick: false
        }
    ];

    constructor(
        private readonly dialog: MatDialog,
        private readonly snackBar: MatSnackBar,
        private readonly atokuService: AtokuService,
        private readonly context: ConsoleContextService
    ) {
        this.refreshUsersAction.subscribe(() => this.refreshUsers());
        this.giveAccessAction.subscribe((user) => this.openAccessDialog(user));
        this.manageDepositAction.subscribe((user) => this.openDepositDialog(user));
        this.toggleSubscriptionAction.subscribe((user) => this.toggleSubscription(user));
        this.toggleBlockedAction.subscribe((user) => this.toggleBlocked(user));
        this.removeUserAction.subscribe((user) => this.removeUser(user));
    }

    ngOnInit(): void {
        this.loadUsers();
    }

    private openAccessDialog(user: CrudAtokuUser): void {
        const dialogRef = this.dialog.open(GiveAccessDialogComponent, {
            width: '500px',
            data: this.toDialogUser(user)
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                return;
            }

            const startDate = this.toUtcIso(result.startDate, result.startTime);
            const endDate = this.toUtcIso(result.endDate, result.endTime);
            const displayName = this.getDisplayName(user);
            const title = `Temporary access for ${displayName}`.trim();

            this.atokuService.createAccessRequest({
                userEmail: user.email,
                spaceCode: 'main-kitchen',
                startDate,
                endDate,
                title,
                reason: title,
                status: 'granted'
            }).subscribe(() => {
                this.snackBar.open(`Temporary access granted for ${displayName}`, 'Close', { duration: 2500 });
            });
        });
    }

    private openDepositDialog(user: CrudAtokuUser): void {
        if (!user.cardGuid) {
            this.snackBar.open('No linked Atoku card was found for this user.', 'Close', { duration: 2500 });
            return;
        }

        const dialogRef = this.dialog.open(DepositDeductionDialogComponent, {
            width: '500px',
            data: {
                name: this.getDisplayName(user),
                email: user.email,
                deposit: user.deposit
            }
        });

        dialogRef.afterClosed().subscribe((result?: DepositDeductionDialogResult) => {
            if (!result) {
                return;
            }

            this.atokuService.deductCardDeposit(user.cardGuid, {
                amount: result.amount,
                currency: 'ZAR',
                reason: result.reason,
                actionedBy: this.getOperatorName()
            }).subscribe({
                next: (updatedCard) => {
                    this.updateUser(user.id, {
                        balance: this.toNumber(updatedCard.balance),
                        deposit: this.toNumber(updatedCard.deposit),
                        availableBalance: this.toNumber(updatedCard.availableBalance)
                    });
                    this.snackBar.open(`Deposit deducted for ${this.getDisplayName(user)}`, 'Close', { duration: 2500 });
                },
                error: (error) => {
                    const message = typeof error?.error === 'string'
                        ? error.error
                        : 'Could not deduct deposit.';
                    this.snackBar.open(message, 'Close', { duration: 3500 });
                }
            });
        });
    }

    private toggleSubscription(user: CrudAtokuUser): void {
        if (user.rawSubscriptionState === 'NaN') {
            this.snackBar.open('Subscription state is not available for this user.', 'Close', { duration: 2500 });
            return;
        }

        const nextState: SubscriptionState = user.rawSubscriptionState === 'subscribed' ? 'suspended' : 'subscribed';
        const request = nextState === 'subscribed'
            ? this.atokuService.subscribeConsoleUser({ email: user.email })
            : this.atokuService.unsubscribeConsoleUser({ email: user.email });

        request.subscribe(() => {
            this.updateUser(user.id, {
                rawSubscriptionState: nextState,
                subscriptionStateLabel: this.toSubscriptionLabel(nextState)
            });
            this.snackBar.open(
                `${this.getDisplayName(user)} ${nextState === 'subscribed' ? 'subscribed' : 'unsubscribed'}`,
                'Close',
                { duration: 2500 }
            );
        });
    }

    private toggleBlocked(user: CrudAtokuUser): void {
        if (user.rawBlocked === null) {
            this.snackBar.open('Blocked state is not available for this user.', 'Close', { duration: 2500 });
            return;
        }

        const nextBlockedState = !user.rawBlocked;
        this.atokuService.blockConsoleUser({ email: user.email, blocked: nextBlockedState })
            .subscribe(() => {
                this.updateUser(user.id, {
                    rawBlocked: nextBlockedState,
                    blockedLabel: this.toBlockedLabel(nextBlockedState)
                });
                this.snackBar.open(
                    `${this.getDisplayName(user)} ${nextBlockedState ? 'blocked' : 'unblocked'}`,
                    'Close',
                    { duration: 2500 }
                );
            });
    }

    private removeUser(user: CrudAtokuUser): void {
        this.atokuService.deleteConsoleUser({ email: user.email }).subscribe(() => {
            this.loadUsers();
            this.snackBar.open(`${this.getDisplayName(user)} removed`, 'Close', { duration: 2500 });
        });
    }

    private refreshUsers(): void {
        this.loadUsers();
    }

    private updateUser(userId: string, patch: Partial<CrudAtokuUser>): void {
        this.tableData = this.tableData.map(user => user.id === userId
            ? new CrudAtokuUser({ ...user, ...patch })
            : user
        );
    }

    private loadUsers(): void {
        forkJoin({
            users: this.atokuService.getConsoleUsers(),
            cards: this.atokuService.getCards()
        }).subscribe(({ users, cards }) => {
            const cardByEmail = new Map<string, AtokuCard>(
                (cards ?? [])
                    .filter(card => !!card.email)
                    .map(card => [card.email.toLowerCase(), card])
            );

            this.tableData = (users.items ?? [])
                .map(item => this.mapUser(item as Record<string, unknown>, cardByEmail));
        });
    }

    private mapUser(raw: Record<string, unknown>, cardByEmail: Map<string, AtokuCard>): CrudAtokuUser {
        const firstName = this.normalizeText(raw['first_name'] ?? raw['firstName']);
        const surname = this.normalizeText(raw['last_name'] ?? raw['lastName']);
        const pricingProfileFromData = this.extractPricingProfileCode(raw['data']);
        const pricingProfileCode = pricingProfileFromData !== 'NaN'
            ? pricingProfileFromData
            : this.normalizeText(raw['pricingProfileCode'] ?? raw['pricingProfile']);
        const rawSubscriptionState = this.toSubscriptionState(raw['subscription_state'] ?? raw['subscriptionState']);
        const rawBlocked = this.toNullableBoolean(raw['blocked']);
        const entriesToday = this.toNullableNumber(raw['entriesToday']);
        const email = this.normalizeText(raw['email']);
        const card = email === 'NaN' ? undefined : cardByEmail.get(email.toLowerCase());

        return new CrudAtokuUser({
            id: String(raw['user_id'] ?? raw['site_user_id'] ?? ''),
            cardId: this.normalizeText(card?.clientReference ?? raw['client_reference'] ?? raw['clientReference']),
            name: firstName,
            surname,
            email,
            pricingProfileCode,
            subscriptionStateLabel: this.toSubscriptionLabel(rawSubscriptionState),
            blockedLabel: this.toBlockedLabel(rawBlocked),
            entriesTodayLabel: typeof entriesToday === 'number' ? String(entriesToday) : 'NaN',
            cardGuid: card?.id ?? '',
            balance: this.toNumber(card?.balance),
            deposit: this.toNumber(card?.deposit),
            availableBalance: this.toNumber(card?.availableBalance),
            rawSubscriptionState,
            rawBlocked
        });
    }

    private toDialogUser(user: CrudAtokuUser): AtokuUser {
        return {
            id: user.id,
            cardId: user.cardId,
            name: user.name,
            surname: user.surname,
            corporate: false,
            signOnDate: new Date(0),
            openBookings: 0,
            lastBooking: new Date(NaN)
        };
    }

    private toSubscriptionState(value: unknown): SubscriptionState {
        const normalized = String(value ?? '').trim().toLowerCase();
        if (normalized === 'subscribed' || normalized === 'suspended') {
            return normalized;
        }
        return 'NaN';
    }

    private toSubscriptionLabel(value: SubscriptionState): string {
        switch (value) {
            case 'subscribed':
                return 'Subscribed';
            case 'suspended':
                return 'Suspended';
            default:
                return 'NaN';
        }
    }

    private toBlockedLabel(value: boolean | null): string {
        if (value === null) {
            return 'NaN';
        }
        return value ? 'Blocked' : 'Active';
    }

    private normalizeText(value: unknown): string {
        const text = String(value ?? '').trim();
        if (!text || text.toLowerCase() === 'not_available') {
            return 'NaN';
        }
        return text;
    }

    private toNullableBoolean(value: unknown): boolean | null {
        if (typeof value === 'boolean') {
            return value;
        }

        const text = String(value ?? '').trim().toLowerCase();
        if (!text || text === 'not_available') {
            return null;
        }
        if (text === 'true') {
            return true;
        }
        if (text === 'false') {
            return false;
        }
        return null;
    }

    private toNullableNumber(value: unknown): number | 'NaN' {
        const text = String(value ?? '').trim().toLowerCase();
        if (!text || text === 'not_available') {
            return 'NaN';
        }

        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 'NaN';
    }

    private toNumber(value: unknown): number {
        const parsed = Number(value ?? 0);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    private extractPricingProfileCode(dataValue: unknown): string {
        const raw = String(dataValue ?? '').trim();
        if (!raw) {
            return '';
        }

        try {
            const parsed = JSON.parse(raw) as Record<string, unknown>;
            return this.normalizeText(
                parsed['pricingProfileCode'] ??
                parsed['pricingProfile'] ??
                parsed['PricingProfileCode'] ??
                parsed['PricingProfile'] ??
                ''
            );
        } catch {
            return '';
        }
    }

    private toUtcIso(date: Date, time: string): string {
        const [hours, minutes] = String(time || '00:00').split(':').map(value => Number(value || 0));
        const next = new Date(date);
        next.setHours(hours, minutes, 0, 0);
        return next.toISOString();
    }

    private getDisplayName(user: CrudAtokuUser): string {
        const fullName = [user.name, user.surname]
            .filter(value => value && value !== 'NaN')
            .join(' ')
            .trim();
        return fullName || user.email;
    }

    private getOperatorName(): string {
        const currentUser = this.context.currentUser;
        const fullName = [currentUser?.firstname, currentUser?.lastname]
            .filter(Boolean)
            .join(' ')
            .trim();
        return fullName || currentUser?.email || 'Soko Console';
    }
}
